import { useEffect } from 'react';

import type { ActiveContent } from '../../types/activeContent';
import { useAudioPlayerStore } from './useAudioPlayerStore';

export const AUDIO_PLAYER_OPEN_REQUEST = 'AUDIO_PLAYER_OPEN_REQUEST';

export interface AudioPlayerIframeMessage {
	type: typeof AUDIO_PLAYER_OPEN_REQUEST;
	activeContent: ActiveContent;
	options?: {
		isUninitialized?: boolean;
		playImmediately?: boolean;
		expandPlaylist?: boolean;
	};
}

const isAudioPlayerIframeMessage = (data: unknown): data is AudioPlayerIframeMessage => {
	if (!data || typeof data !== 'object') {
		return false;
	}
	const message = data as Record<string, unknown>;
	return message.type === AUDIO_PLAYER_OPEN_REQUEST && message.activeContent != null;
};

/**
 * Listens for audio player open requests posted from embedded iframe pages.
 * Should be mounted in the parent frame so that clicking the audio button
 * inside a smart-link iframe triggers the player in the parent frame's context.
 */
export const useAudioPlayerIframeListener = (): void => {
	const { openAudioPlayer } = useAudioPlayerStore();

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (!isAudioPlayerIframeMessage(event.data)) {
				return;
			}
			openAudioPlayer(event.data.activeContent, event.data.options);
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [openAudioPlayer]);
};
